import { eVendorTitleSearchType } from './shared/eVendorTitleSearchType';
import { IKeyCategoryNames } from './IKeyCategoryNames';

export interface IDocumentCategory {
  name: string;
  label: string;
  internalDescription?: string;
  externalDescription?: string;
  isRepeatable: boolean;
  autoPublishToClient: boolean;
  autoPublishToVendor: boolean;
  isInitialDocument: boolean;
  isPayoff: boolean;
  isLegacy: boolean;
  hasGoodThroughDate: boolean;
  isFinalizable: boolean;
  createdAt: Date;
  updatedAt: Date;
  vendorTitleSearch?: eVendorTitleSearchType;
  isVendorPayoff: boolean;
  isVendorTaxCertification: boolean;
  isUploadableByUser: boolean;
}

export type DocumentCategoriesByName = Record<string, IDocumentCategory>;

// These categories names shouldn't change.
// They generally refer to documents that are generated by the system and are key for the order workflow.

export const CommitmentNames: IKeyCategoryNames = { legacyName: 'Commitment', currentName: 'title-commitment' };
export const isCommitmentName = (name: string): boolean => Object.values(CommitmentNames).includes(name);

export const HoldOpenOwnersPolicyNames: IKeyCategoryNames = {
  legacyName: 'HoldOpenOwnersPolicy',
  currentName: 'policy-hold-open-owners-policy-jacket',
};
export const isHoldOpenOwnersPolicyName = (name: string): boolean =>
  Object.values(HoldOpenOwnersPolicyNames).includes(name);

export const LendersPolicyNames: IKeyCategoryNames = {
  legacyName: 'LendersPolicy',
  currentName: 'policy-standard-loan-policy-prepared',
};

export const LongFormLendersPolicyNames: IKeyCategoryNames = {
  legacyName: 'LongFormLendersPolicy',
  currentName: 'policy-long-form-loan-policy-signed-final',
};
export const isLongFormLendersPolicyName = (name: string): boolean =>
  Object.values(LongFormLendersPolicyNames).includes(name);

export const OwnersPolicyNames: IKeyCategoryNames = {
  legacyName: 'OwnersPolicy',
  currentName: 'policy-owners-policy-signed-final',
};

export const HomeownersPolicyName = 'policy-homeowners-policy-signed-final';

export const isOwnersPolicyName = (name: string): boolean =>
  Object.values(OwnersPolicyNames).concat(HomeownersPolicyName).includes(name);

export const ShortFormLendersPolicyNames: IKeyCategoryNames = {
  legacyName: 'ShortFormLendersPolicy',
  currentName: 'policy-short-form-loan-policy-prepared',
};
export const isShortFormLendersPolicyName = (name: string): boolean =>
  Object.values(ShortFormLendersPolicyNames).includes(name);

export const TaxCertificateNames: IKeyCategoryNames = {
  legacyName: 'TaxCertificate',
  currentName: 'search-taxes-tax-certificate',
};
export const isTaxCertificateName = (name: string): boolean => Object.values(TaxCertificateNames).includes(name);

const TitleSearchNames: IKeyCategoryNames = {
  legacyName: 'TitleSearchReport',
  currentName: 'title-title-search-report',
};
export const isTitleSearchName = (name: string): boolean => Object.values(TitleSearchNames).includes(name);

const PacerSearchNames: IKeyCategoryNames = {
  legacyName: 'PACER',
  currentName: 'search-pacer',
};

const PatriotSearchNames: IKeyCategoryNames = {
  legacyName: 'PATRIOT',
  currentName: 'search-patriot-ofac',
};

const CovenantsAndRestrictionsSearchNames: IKeyCategoryNames = {
  legacyName: 'ConditionsCovenantsAndRestrictions',
  currentName: 'search-covenants-ccrs',
};

const SearchPackageNames: IKeyCategoryNames = {
  legacyName: 'SearchPackage',
  currentName: 'search-search-package',
};

export const VendorOrderDocumentNames = [
  PacerSearchNames,
  PatriotSearchNames,
  CovenantsAndRestrictionsSearchNames,
  SearchPackageNames,
  TaxCertificateNames,
  TitleSearchNames,
];

export const getKeyCategoryLabel = (categoryName: string, categoriesByName: DocumentCategoriesByName): any => {
  if (categoriesByName[categoryName]) {
    return categoriesByName[categoryName].label;
  } else {
    throw new Error('Document category not found');
  }
};

export const requiresGoodThroughDate = (categoryName: string | undefined, categoriesByName: DocumentCategoriesByName) =>
  categoryName && categoriesByName[categoryName] && categoriesByName[categoryName].hasGoodThroughDate;

// Gets the label of the category identified by a name.
// Return: the label of the category or an empty string if the category with that name is not found.
export const getCategoryLabelByName = (name: string, categoriesByName: DocumentCategoriesByName): string => {
  const category = categoriesByName[name];
  if (category) {
    return category.label;
  } else {
    return '';
  }
};

// Builds a function that returns the label of a category given a certain name that identifies the category.
export const buildGetCategoryLabelByName = (categoriesByName: DocumentCategoriesByName): ((name: string) => string) => {
  return function (name: string) {
    return getCategoryLabelByName(name, categoriesByName);
  };
};

export const isVendorPayoffByName = (name: string, categoriesByName: DocumentCategoriesByName): boolean => {
  const category = categoriesByName[name];
  return !!category?.isVendorPayoff;
};

export const buildIsVendorPayoffByName = (categoriesByName: DocumentCategoriesByName): ((name: string) => boolean) => {
  return function (name: string) {
    return isVendorPayoffByName(name, categoriesByName);
  };
};

// Gets the description of the category identified by a name.
// Return: the description of the category or an empty string if the category with that name is not found.
export const getCategoryDescriptionByName = (
  name: string,
  categoriesByName: DocumentCategoriesByName
): string | undefined => {
  const category = categoriesByName[name];
  if (category) {
    return category.internalDescription;
  } else {
    return '';
  }
};

export const buildGetCategoryDescriptionByName = (
  categoriesByName: DocumentCategoriesByName
): ((name: string) => string | undefined) => {
  return function (name: string) {
    return getCategoryDescriptionByName(name, categoriesByName);
  };
};
